import type { ChecklistItem, ChecklistNodeLink } from '../types/schema'

const readChecklistLinkId = (candidate: unknown): string | null => {
  if (typeof candidate === 'string' && candidate.trim()) return candidate
  if (
    candidate
    && typeof candidate === 'object'
    && (candidate as { targetType?: unknown }).targetType === 'CHECKLIST'
    && typeof (candidate as { targetId?: unknown }).targetId === 'string'
  ) {
    return (candidate as { targetId: string }).targetId
  }
  return null
}

const readChecklistLinkTitle = (candidate: unknown): string | null => {
  if (
    candidate
    && typeof candidate === 'object'
    && typeof (candidate as { title?: unknown }).title === 'string'
    && (candidate as { title: string }).title.trim()
  ) {
    return (candidate as { title: string }).title
  }
  return null
}

const pushLink = (
  links: ChecklistNodeLink[],
  seen: Set<string>,
  targetId: string,
  title: string,
) => {
  if (seen.has(targetId)) return
  seen.add(targetId)
  links.push({ targetId, targetType: 'CHECKLIST', title })
}

const dependencyIdsFor = (item: ChecklistItem) => [
  ...(item.dependencyNodeIds ?? []),
  ...(item.prerequisites ?? []),
  ...((item.blockedByNodeIds ?? []).map((link) => readChecklistLinkId(link))),
].filter((id): id is string => typeof id === 'string' && id.length > 0)

export const resolveBlockedByLinks = (
  item: ChecklistItem,
  items: readonly ChecklistItem[],
): ChecklistNodeLink[] => {
  const byId = new Map(items.map((candidate) => [candidate.id, candidate]))
  const links: ChecklistNodeLink[] = []
  const seen = new Set<string>()

  ;(item.blockedByNodeIds ?? []).forEach((candidate) => {
    const targetId = readChecklistLinkId(candidate)
    if (!targetId) return
    const title = readChecklistLinkTitle(candidate) ?? byId.get(targetId)?.title ?? 'Missing dependency'
    pushLink(links, seen, targetId, title)
  })

  dependencyIdsFor(item).forEach((targetId) => {
    const title = byId.get(targetId)?.title ?? 'Missing dependency'
    pushLink(links, seen, targetId, title)
  })

  return links
}

export const resolveBlockerForLinks = (
  item: ChecklistItem,
  items: readonly ChecklistItem[],
): ChecklistNodeLink[] => {
  const links: ChecklistNodeLink[] = []
  const seen = new Set<string>()

  ;(item.isBlockerForNodeIds ?? []).forEach((candidate) => {
    const targetId = readChecklistLinkId(candidate)
    if (!targetId) return
    const title = readChecklistLinkTitle(candidate)
      ?? items.find((possibleTarget) => possibleTarget.id === targetId)?.title
      ?? 'Checklist item'
    pushLink(links, seen, targetId, title)
  })

  items.forEach((candidate) => {
    if (candidate.id === item.id) return
    if (dependencyIdsFor(candidate).includes(item.id)) {
      pushLink(links, seen, candidate.id, candidate.title)
    }
  })

  return links
}
